﻿import type { GetMQTTResponse } from '@/api/mqtt.api'
import type { ResetTableData } from '@/api/template.api'
import { TIMEOUT_MS } from '@/constants/devices'

export type RuntimeResetTableRow = Omit<ResetTableData[number], 'bitIndex' | 'defaultValue'> & {
  bitIndex?: string | number | null
  defaultValue: string | number
}
export type RuntimeResetTable = readonly RuntimeResetTableRow[]

/** Rozbicie liczby na 16 bitów, LSB first. */
const to16BitArray = (value: number): number[] => {
  const n = Math.trunc(value)
  return Array.from({ length: 16 }, (_, i) => (n >> i) & 1)
}

const validIndex = (value: number): boolean => Number.isInteger(value) && value >= 0
const validBitIndex = (value: number): boolean => validIndex(value) && value <= 15

/** Dekoduje liczbę spod wskazanego indeksu ramki: bez `bitIndex` wartość wprost, z `bitIndex`
 *  wybrany bit w rozkładzie LSB first.
 *
 *  Wydzielone z `readMqttValue`, bo serie historyczne (`/mqtt/history/today`) dostają z backendu
 *  gotową liczbę spod indeksu i muszą użyć dokładnie tego samego rozkładu bitów, co odczyt live.
 *  `utils/utils.tsx` ma drugą funkcję `to16BitArray` odwracaną przez `.reverse()` — sięgnięcie po
 *  nią dałoby inny bit dla tego samego mapowania. */
export const decodeMqttNumber = (value: number, bitIndex?: number): number | undefined => {
  if (!Number.isFinite(value)) return undefined
  if (bitIndex === undefined) return value

  return validBitIndex(bitIndex) ? to16BitArray(value)[bitIndex] : undefined
}

const normalizeResetBitIndex = (value: string | number | null | undefined): number | null | undefined => {
  if (value === null || value === undefined || String(value).trim() === '') return undefined

  const parsed = Number(value)
  return validBitIndex(parsed) ? parsed : null
}

const readMqttValue = (
  mqtt: GetMQTTResponse[],
  dataType: string,
  index: number,
  bitIndex?: number,
): number | undefined => {
  if (!validIndex(index)) return undefined
  if (bitIndex !== undefined && !validBitIndex(bitIndex)) return undefined

  const item = mqtt.find((measurement) => measurement.type === dataType)
  if (!item) return undefined

  const raw = String(item.value).split(',')[index]
  if (raw === undefined || raw.trim() === '') return undefined

  const parsed = Number(raw)
  if (!Number.isFinite(parsed)) return undefined

  return decodeMqttNumber(parsed, bitIndex)
}

export const getMqttValue = (
  mqtt: GetMQTTResponse[],
  dataType: string,
  index = 0,
  bitIndex?: number,
  fallback = 0,
): number => {
  const item = mqtt.find((measurement) => measurement.type === dataType)
  const createdAt = item ? new Date(item.createdAt).getTime() : Number.NaN

  if (!Number.isFinite(createdAt) || createdAt + TIMEOUT_MS < Date.now()) return fallback

  return readMqttValue(mqtt, dataType, index, bitIndex) ?? fallback
}

export const getResetValue = (
  resetTable: RuntimeResetTable | undefined,
  dataType: string,
  index: number,
  bitIndex?: number,
  fallback = 0,
): number => {
  if (!validIndex(index)) return fallback
  if (bitIndex !== undefined && !validBitIndex(bitIndex)) return fallback

  const requestedBit = bitIndex
  const row = resetTable?.find((candidate) => {
    const candidateBit = normalizeResetBitIndex(candidate.bitIndex)
    return candidate.dataType === dataType && candidate.index === index && candidateBit === requestedBit
  })
  if (!row) return fallback

  const defaultValue = String(row.defaultValue).trim()
  if (defaultValue === '') return 0

  const parsed = Number(defaultValue)
  return Number.isFinite(parsed) ? parsed : 0
}

export type ResolveMqttValueOptions = {
  mqtt: GetMQTTResponse[]
  resetTable?: RuntimeResetTable
  useResetValues: boolean
  dataType: string
  index?: number
  bitIndex?: number
}

export const resolveMqttValue = ({
  mqtt,
  resetTable,
  useResetValues,
  dataType,
  index = 0,
  bitIndex,
}: ResolveMqttValueOptions): number => {
  const lastValue = readMqttValue(mqtt, dataType, index, bitIndex) ?? 0

  if (!useResetValues) return lastValue

  return getResetValue(resetTable, dataType, index, bitIndex, lastValue)
}

/** Stan alertu urządzenia z rekordu `type: 'alert'`. */
export const getAlertValue = (mqtt: GetMQTTResponse[]): 'normal' | 'alarm' | 'error' => {
  const item = mqtt.find((m) => m.type === 'alert')
  return item?.value === 'alarm' || item?.value === 'error' ? item.value : 'normal'
}

/** Ułamek zakresu [0..1] — dla wykresów kołowych i pasków. */
export const rangeFraction = (value: number, min: number, max: number): number => {
  if (max <= min) return 0
  return Math.min(1, Math.max(0, (value - min) / (max - min)))
}
